import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Dimensions,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path, Defs, LinearGradient, Stop, Line, Text as SvgText } from 'react-native-svg';
import { Colors } from '@constants/colors';
import { StockQuote, StockChartData, ChartTimeframe } from '../../types/stock.types';
import { formatVolume, formatMarketCap } from '@utils/formatters';
import { TIMEFRAME_OPTIONS } from '@/constants/chartTimeframes';

interface StockChartProps {
  symbol: string;
  data: StockChartData[];
  quote: StockQuote;
  loading?: boolean;
  selectedTimeframe?: ChartTimeframe;
  onTimeframeChange?: (timeframe: ChartTimeframe) => void;
  timeframeLoading?: boolean;
}

/**
 * Stat Item Component
 */
interface StatItemProps {
  label: string;
  value: string;
}

const StatItem: React.FC<StatItemProps> = ({ label, value }) => (
  <View style={styles.statItem}>
    <Text style={styles.statLabel}>{label}</Text>
    <Text style={styles.statValue}>{value}</Text>
  </View>
);

/**
 * Generate SVG path for area chart
 */
const generateChartPath = (
  data: StockChartData[],
  width: number,
  height: number,
  padding: number = 10
): { linePath: string; areaPath: string; minValue: number; maxValue: number } => {
  if (data.length === 0) {
    return { linePath: '', areaPath: '', minValue: 0, maxValue: 0 };
  }

  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  // Get min and max values for scaling
  const values = data.map(d => d.close);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue || 1; // Avoid division by zero

  // Calculate points
  const points = data.map((d, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((d.close - minValue) / valueRange) * chartHeight;
    return { x, y };
  });

  // Generate line path
  const linePath = points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x},${point.y}`;
    }
    return `${path} L ${point.x},${point.y}`;
  }, '');

  // Generate area path (line path + bottom line)
  const areaPath = `${linePath} L ${points[points.length - 1].x},${height - padding} L ${padding},${height - padding} Z`;

  return { linePath, areaPath, minValue, maxValue };
};

/**
 * Stock Chart Component
 * Displays real-time stock data with native SVG chart and timeframe selector
 */
export const StockChart: React.FC<StockChartProps> = ({
  symbol,
  data,
  quote,
  loading = false,
  selectedTimeframe = '1D',
  onTimeframeChange,
  timeframeLoading = false,
}) => {
  const screenWidth = Dimensions.get('window').width;
  const chartWidth = screenWidth - 48 - 32; // Account for margins and padding
  const chartHeight = 180;

  // Debug logging
  if (__DEV__) {
    console.log('📊 StockChart Render:', {
      symbol,
      dataLength: data?.length || 0,
      hasQuote: !!quote,
      loading,
      firstDataPoint: data?.[0],
    });
  }

  const { linePath, areaPath, minValue, maxValue } = useMemo(() => {
    if (!data || data.length === 0) {
      return { linePath: '', areaPath: '', minValue: 0, maxValue: 0 };
    }
    return generateChartPath(data, chartWidth, chartHeight);
  }, [data, chartWidth, chartHeight]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.ai.primary} />
        <Text style={styles.loadingText}>Loading chart...</Text>
      </View>
    );
  }

  const isProfit = quote.change >= 0;
  const chartColor = isProfit ? Colors.market.profit : Colors.market.loss;

  return (
    <View style={styles.container}>
      {/* Header with stock info */}
      <View style={styles.header}>
        <View style={styles.symbolRow}>
          <Text style={styles.symbol}>{symbol}</Text>
          <View
            style={[
              styles.badge,
              {
                backgroundColor: isProfit ? `${Colors.market.profit}20` : `${Colors.market.loss}20`,
              },
            ]}
          >
            <Text
              style={[
                styles.badgeText,
                { color: isProfit ? Colors.market.profit : Colors.market.loss },
              ]}
            >
              {isProfit ? '▲' : '▼'} {Math.abs(quote.changePercent).toFixed(2)}%
            </Text>
          </View>
        </View>
        <Text style={styles.companyName} numberOfLines={1}>
          {quote.companyName}
        </Text>
        <View style={styles.priceRow}>
          <Text style={styles.price}>${quote.latestPrice.toFixed(2)}</Text>
          <Text
            style={[styles.change, { color: isProfit ? Colors.market.profit : Colors.market.loss }]}
          >
            {isProfit ? '+' : ''}${quote.change.toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Timeframe Selector - Subtle pill buttons */}
      {onTimeframeChange && (
        <View style={styles.timeframeSelector}>
          {TIMEFRAME_OPTIONS.map(timeframe => {
            const isSelected = timeframe === selectedTimeframe;
            return (
              <TouchableOpacity
                key={timeframe}
                style={[styles.timeframeButton, isSelected && styles.timeframeButtonActive]}
                onPress={() => onTimeframeChange(timeframe)}
                disabled={timeframeLoading}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.timeframeButtonText,
                    isSelected && styles.timeframeButtonTextActive,
                  ]}
                >
                  {timeframe}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* Chart */}
      {timeframeLoading ? (
        <View style={styles.chartContainer}>
          <View style={styles.timeframeLoadingContainer}>
            <ActivityIndicator size="small" color={Colors.accent.primary} />
            <Text style={styles.timeframeLoadingText}>Loading {selectedTimeframe} data...</Text>
          </View>
        </View>
      ) : data && data.length > 0 ? (
        <View style={styles.chartContainer}>
          <Svg width={chartWidth} height={chartHeight}>
            <Defs>
              <LinearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0%" stopColor={chartColor} stopOpacity="0.5" />
                <Stop offset="100%" stopColor={chartColor} stopOpacity="0" />
              </LinearGradient>
            </Defs>

            {/* Grid lines */}
            <Line
              x1="10"
              y1={chartHeight / 2}
              x2={chartWidth - 10}
              y2={chartHeight / 2}
              stroke={Colors.border.subtle}
              strokeWidth="1"
              strokeDasharray="5,5"
            />

            {/* Area fill */}
            <Path d={areaPath} fill="url(#chartGradient)" />

            {/* Line */}
            <Path d={linePath} stroke={chartColor} strokeWidth="2" fill="none" />
          </Svg>

          {/* Price labels */}
          <View style={styles.priceLabels}>
            <Text style={styles.priceLabel}>${maxValue.toFixed(2)}</Text>
            <Text style={styles.priceLabel}>${minValue.toFixed(2)}</Text>
          </View>
        </View>
      ) : (
        <View style={styles.noChartData}>
          <Text style={styles.noChartText}>
            {data === undefined ? 'Loading chart data...' : 'Chart data unavailable'}
          </Text>
        </View>
      )}

      {/* Quick stats */}
      <View style={styles.stats}>
        <StatItem label="High" value={`$${quote.high.toFixed(2)}`} />
        <StatItem label="Low" value={`$${quote.low.toFixed(2)}`} />
        <StatItem label="Volume" value={formatVolume(quote.volume)} />
        <StatItem label="Mkt Cap" value={formatMarketCap(quote.marketCap)} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.default,
    padding: 16,
    shadowColor: Colors.ai.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
  },
  loadingContainer: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.default,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.text.secondary,
  },
  header: {
    marginBottom: 16,
  },
  symbolRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  symbol: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Colors.text.primary,
    marginRight: 12,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  companyName: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginBottom: 8,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 12,
  },
  price: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Colors.text.primary,
  },
  change: {
    fontSize: 16,
    fontWeight: '600',
  },
  chartContainer: {
    height: 180,
    marginBottom: 16,
    position: 'relative',
  },
  priceLabels: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    justifyContent: 'space-between',
    paddingVertical: 10,
  },
  priceLabel: {
    fontSize: 10,
    color: Colors.text.tertiary,
    backgroundColor: Colors.background.secondary,
    paddingHorizontal: 4,
  },
  noChartData: {
    height: 180,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: `${Colors.background.tertiary}80`,
    borderRadius: 8,
    marginBottom: 16,
  },
  noChartText: {
    fontSize: 14,
    color: Colors.text.tertiary,
  },
  stats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 11,
    color: Colors.text.tertiary,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  // Timeframe Selector Styles - Subtle design
  timeframeSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 12,
    paddingVertical: 8,
  },
  timeframeButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    backgroundColor: Colors.background.tertiary,
    minWidth: 36,
    alignItems: 'center',
  },
  timeframeButtonActive: {
    backgroundColor: Colors.accent.primary,
  },
  timeframeButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.text.tertiary,
  },
  timeframeButtonTextActive: {
    color: Colors.text.primary,
  },
  // Timeframe Loading Indicator
  timeframeLoadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  timeframeLoadingText: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
});
